import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Calendar, Store as StoreIcon, ExternalLink, FileText, ShieldCheck, ShieldAlert } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function StatusPill({ status }) {
  const map = {
    approved: "pf-chip-mint",
    pending: "pf-chip-lav",
    rejected: "",
  };
  const cls = map[status] || "";
  return <span className={`pf-chip ${cls}`}>{status || "unknown"}</span>;
}

export default function AdminOnboarding() {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const q = filter === "all" ? "" : `?status=${filter}`;
    const { data } = await api.get(`/admin/onboarding${q}`);
    setRows(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (storeId) => {
    await api.post(`/admin/stores/${storeId}/approve`);
    toast.success("Brand approved & listed");
    load();
  };
  const reject = async (storeId) => {
    await api.post(`/admin/stores/${storeId}/reject`);
    toast.success("Brand rejected");
    load();
  };
  const approveVerification = async (storeId) => {
    await api.post(`/admin/stores/${storeId}/verify`);
    toast.success("Verification approved");
    load();
  };
  const rejectVerification = async (storeId) => {
    await api.post(`/admin/stores/${storeId}/verify/reject`);
    toast.success("Verification rejected — merchant notified");
    load();
  };

  const openDoc = async (url) => {
    // fetch with cookies then open blob
    try {
      const resp = await api.get(url.replace(/^\/api/, ""), { responseType: "blob" });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, "_blank");
    } catch {
      toast.error("Unable to load document");
    }
  };

  const counts = {
    all: rows.length,
    pending: rows.filter((r) => r.store_status === "pending").length,
    approved: rows.filter((r) => r.store_status === "approved").length,
    rejected: rows.filter((r) => r.store_status === "rejected").length,
  };

  return (
    <div data-testid="admin-onboarding">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="pf-chip pf-chip-cyan">Master console</div>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Brand onboarding</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">Every new brand joining Cyannect appears here. Review, approve or reject in one click.</p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total brands", value: counts.all, chip: "pf-chip-cyan" },
          { label: "Pending review", value: rows.filter(r => r.store_status === "pending").length, chip: "pf-chip-lav" },
          { label: "Approved", value: rows.filter(r => r.store_status === "approved").length, chip: "pf-chip-mint" },
          { label: "Pending coupons", value: rows.reduce((s, r) => s + (r.coupon_pending || 0), 0), chip: "" },
        ].map((s, i) => (
          <div key={i} className="pf-card p-5">
            <span className={`pf-chip ${s.chip} w-fit`}>{s.label}</span>
            <div className="font-display text-4xl font-medium mt-4 text-slate-900 tabular-nums">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          ["all", "All"],
          ["pending", "Pending"],
          ["approved", "Approved"],
          ["rejected", "Rejected"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest transition-all ${filter === k ? "pf-btn-primary text-black font-bold" : "pf-btn-glass"}`}
            data-testid={`onboarding-filter-${k}`}
          >
            {label} <span className="ml-1 opacity-75">{counts[k]}</span>
          </button>
        ))}
      </div>

      {/* Brand list */}
      {loading ? (
        <div className="text-slate-500 py-8">Loading brands…</div>
      ) : rows.length === 0 ? (
        <div className="pf-card p-12 text-center">
          <StoreIcon className="w-8 h-8 mx-auto text-cyan-600 mb-3" />
          <div className="text-slate-900 font-medium">No brands yet</div>
          <div className="text-sm text-slate-500 mt-1">When a merchant signs up, they will appear here.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.user_id} className="pf-card p-5 flex flex-col md:flex-row md:items-center gap-5" data-testid={`brand-row-${r.user_id}`}>
              <img
                src={r.store?.logo_url || `https://api.dicebear.com/7.x/shapes/svg?seed=${r.email}`}
                alt={r.store?.name || r.name}
                className="w-14 h-14 rounded-2xl border border-white/80 bg-white object-contain shadow-sm"
                data-logo="1"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-xl font-medium text-slate-900 truncate">
                    {r.store?.name || r.name}
                  </div>
                  <StatusPill status={r.store_status || "no store"} />
                  {r.store?.verification && (
                    <span className={`pf-chip ${r.store.verification.status === "approved" ? "pf-chip-mint" : r.store.verification.status === "submitted" ? "pf-chip-lav" : r.store.verification.status === "rejected" ? "" : ""}`}>
                      {r.store.verification.status === "approved" && <ShieldCheck className="w-3 h-3" />}
                      {r.store.verification.status === "submitted" && <ShieldAlert className="w-3 h-3" />}
                      verify · {r.store.verification.status.replace("_", " ")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 flex-wrap">
                  <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {r.email}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Joined {timeAgo(r.joined_at)}</span>
                  {r.store?.website && (
                    <a href={r.store.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-cyan-700">
                      <ExternalLink className="w-3.5 h-3.5" /> {r.store.website.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                </div>
                {r.store?.description && (
                  <p className="text-sm text-slate-600 mt-2 line-clamp-1">{r.store.description}</p>
                )}
                {r.store?.verification?.status === "submitted" && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap glass rounded-xl p-3">
                    <FileText className="w-4 h-4 text-cyan-700" />
                    <div className="flex-1 text-sm text-slate-700">
                      <div><span className="text-slate-500">Business #:</span> <span className="font-mono">{r.store.verification.business_number}</span></div>
                      <div className="text-xs text-slate-500 mt-0.5">Uploaded {timeAgo(r.store.verification.submitted_at)} · {r.store.verification.document_filename}</div>
                    </div>
                    <button onClick={() => openDoc(r.store.verification.document_url)} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1" data-testid={`view-doc-${r.user_id}`}>
                      View doc <ExternalLink className="w-3 h-3" />
                    </button>
                    <button onClick={() => approveVerification(r.store.id)} className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold" data-testid={`verify-approve-${r.user_id}`}>
                      Verify
                    </button>
                    <button onClick={() => rejectVerification(r.store.id)} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold" data-testid={`verify-reject-${r.user_id}`}>
                      Reject
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-6 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Coupons</div>
                  <div className="font-display text-2xl text-slate-900 tabular-nums">{r.coupon_total}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Pending</div>
                  <div className="font-display text-2xl text-cyan-700 tabular-nums">{r.coupon_pending}</div>
                </div>
              </div>

              <div className="flex gap-2 md:w-56 md:justify-end">
                {r.store && r.store.status !== "approved" && (
                  <button onClick={() => approve(r.store.id)} className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold" data-testid={`approve-brand-${r.user_id}`}>
                    Approve
                  </button>
                )}
                {r.store && r.store.status !== "rejected" && (
                  <button onClick={() => reject(r.store.id)} className="pf-btn-glass px-4 py-2 rounded-full text-xs font-semibold" data-testid={`reject-brand-${r.user_id}`}>
                    Reject
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
